import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayCircle, Users, Trophy, Calendar, ChevronRight, Plus, ListChecks } from 'lucide-react';
import HostLayout from '@/components/mafia/HostLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HostDashboardPage() {
  const navigate = useNavigate();
  const { host, loading } = useMafiaAuth();
  const [board, setBoard] = useState([]);
  const [games, setGames] = useState([]);

  useEffect(() => { if (!loading && !host) navigate('/mafia/login', { replace: true }); }, [loading, host, navigate]);

  useEffect(() => {
    if (!host) return;
    (async () => {
      try {
        const [lb, g] = await Promise.all([
          fetch(`${API}/api/mafia/auth/leaderboard?limit=5`).then(r => r.json()),
          fetch(`${API}/api/mafia/games`).then(r => r.json()),
        ]);
        setBoard(lb.leaderboard || []);
        setGames(g.games || []);
      } catch { /* ignore */ }
    })();
  }, [host]);

  if (loading || !host) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;

  const finished = games.filter(g => g.status === 'ended').length;
  const active = games.length - finished;

  return (
    <HostLayout title={`Добро пожаловать, ${host.name || 'ведущий'}`} subtitle="Обзор"
      actions={
        <button onClick={() => navigate('/mafia/new')} data-testid="host-new-game-btn" className="ms-btn ms-btn-primary">
          <Plus size={13} /> Новая игра
        </button>
      }
    >
      {/* KPI grid */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Активные столы" value={active} icon={PlayCircle} accent testid="kpi-active" />
        <Kpi label="Завершённые" value={finished} icon={Calendar} testid="kpi-finished" />
        <Kpi label="Всего игр" value={games.length} icon={Trophy} testid="kpi-total" />
        <Kpi label="Игроков" value={board.length} icon={Users} testid="kpi-players" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active games */}
        <section className="ms-card lg:col-span-2">
          <div className="px-5 py-4 ms-border-b flex items-center justify-between">
            <p className="ms-kicker">Активные столы</p>
            <button onClick={() => navigate('/host/games')} className="text-xs ms-text-2 hover:ms-text font-medium flex items-center gap-1">
              Все <ChevronRight size={12} />
            </button>
          </div>
          {games.filter(g => g.status !== 'ended').length === 0 ? (
            <p className="px-5 py-8 text-sm ms-text-2 text-center">Нет активных столов. Создайте новую игру выше.</p>
          ) : (
            <div className="ms-divide">
              {games.filter(g => g.status !== 'ended').slice(0, 5).map(g => (
                <button key={g.id} onClick={() => navigate(`/mafia/game/${g.id}`)}
                  className="w-full grid grid-cols-12 items-center px-5 py-3 text-left hover:bg-[var(--ms-surface-2)] transition-colors">
                  <span className="col-span-5 text-sm ms-text truncate">{g.name || 'Без названия'}</span>
                  <span className="col-span-3 text-xs ms-text-2">{g.players?.length || 0} мест</span>
                  <span className="col-span-2 text-xs ms-text-3 capitalize">{g.phase || '—'}</span>
                  <span className="col-span-2 text-right"><span className="ms-chip ms-chip-red">{g.status}</span></span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Top players preview */}
        <section className="ms-card">
          <div className="px-5 py-4 ms-border-b flex items-center justify-between">
            <p className="ms-kicker">Топ игроков</p>
            <button onClick={() => navigate('/host/players')} className="text-xs ms-text-2 hover:ms-text font-medium flex items-center gap-1">
              Все <ChevronRight size={12} />
            </button>
          </div>
          {board.length === 0 ? (
            <p className="px-5 py-8 text-sm ms-text-2 text-center">Пока нет игроков.</p>
          ) : (
            <div className="ms-divide">
              {board.slice(0, 5).map((p, i) => (
                <div key={p.member_no} className="flex items-center gap-3 px-5 py-3">
                  <span className="ms-text-3 tabular-nums w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm ms-text truncate">{p.name}</p>
                    <p className="text-[11px] ms-text-3">{p.member_no}</p>
                  </div>
                  <span className="text-sm font-semibold tabular-nums ms-text-red">{p.points}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Quick actions */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <QuickAction icon={ListChecks} label="Библиотека ролей" onClick={() => navigate('/host/roles')} />
        <QuickAction icon={Users} label="Все игры" onClick={() => navigate('/host/games')} />
        <QuickAction icon={PlayCircle} label="Открыть Салон" onClick={() => navigate('/mafia')} accent />
      </section>
    </HostLayout>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="ms-card ms-card-hover p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <p className="ms-kicker">{label}</p>
        <Icon size={14} className={accent ? 'ms-text-red' : 'ms-text-3'} />
      </div>
      <p className={`ms-display text-3xl tabular-nums ${accent ? 'ms-text-red' : ''}`}>{value}</p>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, accent }) {
  return (
    <button onClick={onClick} className={`ms-card p-5 flex items-center justify-between transition-all ${accent ? '' : 'ms-card-hover'}`}
      style={accent ? { background: 'var(--ms-red)', borderColor: 'var(--ms-red)' } : {}}>
      <span className={`flex items-center gap-3 font-medium ${accent ? 'text-white' : 'ms-text'}`}>
        <Icon size={16} /> {label}
      </span>
      <ChevronRight size={16} className={accent ? 'text-white' : 'ms-text-3'} />
    </button>
  );
}
